import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import type { SpreaderMatrixInstrument } from '@/state/widget-instances'
import type { SelectedInstrument } from '@/types/dashboard'
import { Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'

// Metadata-first — NOT a symbol regex. `isPerpInstrument` (features/hedge/lib/
// instrument-display.ts) is the same perpetual-detection the watchlist wizard's own
// grouping uses, so Spreader agrees with it rather than keeping a third parallel
// implementation.
export function resolveInstrumentKindLabel(
  instrument: Instrument | undefined,
  symbol: string
): string {
  if (!instrument) return symbol.toLowerCase().includes('perp') ? 'perp' : 'spot'
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return 'option'
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
    return isPerpInstrument(instrument) ? 'perp' : 'future'
  }
  return 'spot'
}

/**
 * Builds a spreader-tagged `SelectedInstrument` from a picked (exchange, symbol) pair.
 * Shared by the spread-matrix widget (its own row×column cells) and the order form's
 * "+ Add leg" picker (`use-unified-order-form.ts`'s `addSpreaderLeg`) — both need the
 * exact same instrument-metadata resolution, so this stays a single implementation
 * rather than drifting into two.
 */
export function buildSpreaderSelectedInstrument(
  matrixInst: SpreaderMatrixInstrument,
  side: 'buy' | 'sell',
  role: 'quoting' | 'taking',
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): SelectedInstrument {
  const exchangeInstrumentMap = multiExchangeInstrumentMap.get(matrixInst.exchange)
  const instrument: Instrument | undefined = exchangeInstrumentMap?.get(matrixInst.symbol)
  return {
    symbol: matrixInst.symbol,
    // A fresh matrix-cell/leg pick — no bound source account.
    accountBinding: { kind: 'inherited' },
    side,
    baseCurrency: resolveBaseCurrency(exchangeInstrumentMap ?? [], matrixInst.symbol, instrument),
    quoteCurrency: resolveQuoteCurrency(exchangeInstrumentMap ?? [], matrixInst.symbol, instrument),
    instrumentKind: resolveInstrumentKindLabel(instrument, matrixInst.symbol),
    exchange: String(matrixInst.exchange),
    exchangeSymbol: instrument?.exchangeSymbol,
    exchangeId: instrument?.exchangeId,
    internalId: instrument?.internalId,
    settlementCurrency: instrument?.settlementCurrency,
    tickSize: instrument?.tickSize,
    minTradeAmount: instrument?.minTradeAmount,
    contractSize: instrument?.contractSize,
    pricePrecision: instrument?.pricePrecision,
    qtyPrecision: instrument?.qtyPrecision,
    type: instrument?.type,
    spreaderRole: role,
  }
}
